import React from 'react';
import { useNavigate } from 'react-router-dom';
import { iconMap } from '../utils/iconMap';
import slugify from '../utils/slugify';
import type { SubMenuItem } from '../data/menuData';

interface SubMenuProps {
  items: SubMenuItem[];
  onSelect?: (label: string) => void;
}

const SubMenu: React.FC<SubMenuProps> = ({ items, onSelect }) => {
  const navigate = useNavigate();
  const showLimit = 6;
  const limitedItems = items.slice(0, showLimit);
  const showAllButton = items.length > showLimit;

  return (
    <div className="bg-white dark:bg-gray-900 shadow-xl border border-gray-200 dark:border-gray-700 rounded-lg min-w-[220px] max-w-[280px] max-h-[70vh] overflow-hidden z-50">
      {/* Compact Header */}
      <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
            Categories
          </h3>
          <span className="text-xs text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-700 px-1.5 py-0.5 rounded">
            {items.length}
          </span>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="overflow-y-auto max-h-[calc(70vh-80px)]">
        {/* Category Items - Fixed Hover */}
        <div className="p-1">
          {limitedItems.map((item, i) => {
            const raw = item.link.replace(/^\/?category\//i, '').trim();
            const slug = slugify(raw);
            const IconComponent = item.icon ? iconMap[item.icon] : null;

            return (
              <button
                key={i}
                onClick={() => {
                  navigate(`/categories/${slug}`);
                  onSelect?.(item.label);
                }}
                className="w-full flex items-center gap-2 p-2 rounded-md hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors duration-200 group relative"
              >
                {/* Compact Icon */}
                {IconComponent && (
                  <div className="w-6 h-6 bg-gradient-to-br from-purple-500 to-purple-600 rounded flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform duration-200">
                    <IconComponent className="w-3 h-3 text-white" />
                  </div>
                )}
                
                {/* Fallback icon */}
                {!IconComponent && (
                  <div className="w-6 h-6 bg-gray-400 rounded flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform duration-200">
                    <span className="text-white text-xs font-bold">
                      {item.label.charAt(0)}
                    </span>
                  </div>
                )}
                
                {/* Compact Text */}
                <div className="flex-1 min-w-0 text-left">
                  <div className="text-sm text-gray-700 dark:text-gray-300 font-medium group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors duration-200 truncate">
                    {item.label}
                  </div>
                </div>

                {/* Small Arrow - Always visible but subtle */}
                <svg 
                  className="w-3 h-3 text-gray-300 group-hover:text-purple-500 group-hover:translate-x-0.5 transition-all duration-200 flex-shrink-0" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            );
          })}
        </div>

        {/* Show All Button - Fixed Hover */}
        {showAllButton && (
          <div className="border-t border-gray-100 dark:border-gray-800 mx-2 pt-2">
            <button
  onClick={() => {
    navigate('/categories');
    onSelect?.('All Categories');
  }}
  className="w-full flex items-center justify-center gap-3 py-3 px-4 bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5 group"
>
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
  </svg>
  <span>View All Categories</span>
  <svg 
    className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" 
    fill="none" 
    stroke="currentColor" 
    viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
</button>



          </div>
        )}
      </div>

      {/* No items state */}
      {items.length === 0 && (
        <div className="text-center py-4 px-3">
          <div className="w-8 h-8 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-2">
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
          </div>
          <p className="text-gray-500 dark:text-gray-400 text-xs">No categories available</p>
        </div>
      )}
    </div>
  );
};

export default SubMenu;